import { useTranslation } from 'react-i18next';
import { type ReactElement, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest, useWorkspace } from '~/app/providers';
import { BrandLogo } from '~/components/ui/BrandLogo';
import { CompanyForm } from './CompanyForm';
import { uploadPrivateAsset } from '~/repositories/assetRepository';

export const OnboardingPage = (): ReactElement => {
  const { t } = useTranslation('onboarding');
  const { t: ta } = useTranslation('account');
  const { createInitialCompany } = useWorkspace();
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo') || '/';
  return (
    <main className="min-h-screen bg-surface p-5 text-foreground sm:p-10">
      <div className="mx-auto max-w-4xl">
        <BrandLogo />
        <div className="mb-8 mt-14">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-primary-300">
            01 / {t('step')}
          </p>
          <h1 className="text-4xl font-semibold tracking-[-0.04em] text-foreground">
            {t('title')}
          </h1>
          <p className="mt-3 text-muted">{t('subtitle')}</p>
        </div>
        <section className="rounded-3xl border border-border bg-elevated p-5 shadow-[var(--studio-shadow)] sm:p-8">
          <CompanyForm
            onSubmit={company => {
              if (submitting) return;
              setSubmitting(true);
              setSubmissionError('');
              const { logoFile, ...workspace } = company;
              void createInitialCompany(workspace)
                .then(async created => {
                  if (logoFile) {
                    try {
                      const logo = await uploadPrivateAsset(
                        created.id,
                        logoFile,
                        'IMAGE',
                        'PRODUCT_LOGO'
                      );
                      await apiRequest(`/workspaces/${created.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ logoAssetId: logo.id }),
                      });
                    } catch (error) {
                      // TODO: Surface a retry action after remote object storage is configured.
                      console.warn(
                        'Workspace created without its optional logo.',
                        error
                      );
                    }
                  }
                  navigate(returnTo);
                })
                .catch(() => {
                  setSubmissionError(ta('createWorkspaceFailed'));
                  setSubmitting(false);
                });
            }}
            submitLabel={t('create')}
            submitting={submitting}
            submissionError={submissionError}
          />
        </section>
      </div>
    </main>
  );
};
